export interface ProjectPathSegments {
  cohortId?: string;
  cohortVersionId?: string;
  variableId?: string;
  conceptGroupId?: string;
  /** OR-6126: a *proposed* (preview) concept group under review, not yet created. */
  conceptGroupPreviewId?: string;
  datasetId?: string;
  datasetVersionId?: string;
}

/**
 * Parse the artifact-detail segments from a `/projects/:projectId/...` path.
 * Tolerant of partial paths that show up mid-navigation AND of the legacy URL
 * form (`/cohorts/:id/cohort-version/:vId/cohort-builder`) that surfaces when
 * the cohort-builder's tab nav navigates back to its legacy paths under the
 * agent-first shell. Both `/cohort/:id` (agent-first) and `/cohorts/:id`
 * (legacy) resolve to the same cohort id. Likewise for `/variable/:avId`
 * (agent-first) vs `/analytical-variable/:avId/edit` (legacy).
 *
 * Used by `useProjectBreadcrumbs` (for crumb names) and `useCurrentScope`
 * (for agent context).
 */
export const parseProjectPathSegments = (
  pathname: string,
  projectId: string
): ProjectPathSegments => {
  const prefix = `/projects/${projectId}/`;
  if (!pathname.startsWith(prefix)) return {};
  const tail = pathname.slice(prefix.length).split('?')[0];
  const parts = tail.split('/').filter(Boolean);

  const segments: ProjectPathSegments = {};
  for (let i = 0; i < parts.length; i++) {
    const seg = parts[i];
    const next = parts[i + 1];
    if (!next) break;
    switch (seg) {
      case 'cohort':
      case 'cohorts':
        segments.cohortId = next;
        i++;
        break;
      case 'cohort-version':
        segments.cohortVersionId = next;
        i++;
        break;
      case 'variable':
      case 'analytical-variable':
        segments.variableId = next;
        i++;
        break;
      case 'concept-group':
        segments.conceptGroupId = next;
        i++;
        break;
      case 'concept-group-preview':
        segments.conceptGroupPreviewId = next;
        i++;
        break;
      // `dataset` is the agent-scope placeholder route; `analytical-datasets`
      // is the real AD detail route
      // (`/analytical-datasets/:profileId/analytical-dataset-version/:versionId`).
      // Both resolve to the same datasetId; the version segment is a routing
      // concern (skipped from breadcrumbs, like cohort-version).
      case 'dataset':
      case 'analytical-datasets':
        segments.datasetId = next;
        i++;
        break;
      case 'analytical-dataset-version':
        segments.datasetVersionId = next;
        i++;
        break;
      default:
        break;
    }
  }
  return segments;
};
